import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import VerifiedIcon from '@mui/icons-material/Verified';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import * as volunteerApi from '../../services/volunteerApi';
import { formatDate } from '../../utils/helpers';
import { VolunteerProfile } from '../../types/volunteer';

const VolunteerProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSkill, setNewSkill] = useState('');
  const [addingSkill, setAddingSkill] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await volunteerApi.getVolunteerProfile();
      setProfile(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleAddSkill = async () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;

    try {
      setAddingSkill(true);
      await volunteerApi.addSkill(trimmed);
      setNewSkill('');
      setSnackbar({ open: true, message: 'Skill added', severity: 'success' });
      fetchProfile();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to add skill',
        severity: 'error',
      });
    } finally {
      setAddingSkill(false);
    }
  };

  const handleRemoveSkill = async (skillId: string) => {
    try {
      await volunteerApi.removeSkill(skillId);
      setSnackbar({ open: true, message: 'Skill removed', severity: 'success' });
      fetchProfile();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to remove skill',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Failed to load profile'}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Volunteer Profile
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {profile.totalHours}
              </Typography>
              <Typography color="text.secondary">Total Hours</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {profile.totalShifts}
              </Typography>
              <Typography color="text.secondary">Completed Shifts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <UpcomingIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {profile.upcomingShifts}
              </Typography>
              <Typography color="text.secondary">Upcoming Shifts</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Volunteer Info */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Volunteer Information
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography color="text.secondary">Status:</Typography>
            <Chip
              label={profile.volunteerStatus === 'approved' ? 'Active Volunteer' : profile.volunteerStatus}
              color={profile.volunteerStatus === 'approved' ? 'success' : 'default'}
              size="small"
            />
          </Box>
          {profile.volunteerSince && (
            <Typography color="text.secondary">
              Volunteer since: {formatDate(profile.volunteerSince)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Skills & Certifications */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Skills & Certifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add skills or certifications you have. Admins can verify them.
          </Typography>

          <Box display="flex" gap={1} mb={2}>
            <TextField
              size="small"
              placeholder="e.g., First Aid, DBS Checked, Food Safety"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddSkill();
              }}
              fullWidth
              disabled={addingSkill}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSkill}
              disabled={!newSkill.trim() || addingSkill}
            >
              Add
            </Button>
          </Box>

          {profile.skills.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No skills added yet. Add your skills and certifications above.
            </Typography>
          ) : (
            <List>
              {profile.skills.map((skill, index) => (
                <React.Fragment key={skill.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {skill.name}
                          {skill.verified && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="Verified"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={`Added ${new Date(skill.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveSkill(skill.id)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VolunteerProfilePage;
